import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useFeatureFlags } from '@/contexts/FeaturesContext';

const getStoredRole = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.role || '';
  } catch {
    return '';
  }
};

/**
 * FeatureGatedRoute - Gates a route based on the `app_features` config
 * for a given feature id:
 *   - If `superadmin_only` is set, requires user.role === 'superadmin'.
 *   - If `is_premium` is set, requires an active subscription.
 * Falls back to SubscriptionRoute behaviour otherwise.
 */
export const FeatureGatedRoute = ({ children, featureId }) => {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const { isPremium, loading: isLoading, tier } = useSubscription();
  const flags = useFeatureFlags(featureId);

  if (!token) {
    return <Navigate to="/subscription" state={{ from: location.pathname, requiresSubscription: true }} replace />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  // Superadmin-only feature: non-superadmins get sent home.
  if (flags.superadminOnly) {
    const role = getStoredRole();
    if (role !== 'superadmin') {
      return <Navigate to="/" replace />;
    }
    // Superadmin bypasses subscription check for restricted features.
    return children;
  }

  if (flags.isPremium) {
    if (isPremium || tier === 'premium' || tier === 'lifetime') {
      return children;
    }
    return <Navigate to="/subscription" state={{ from: location.pathname, requiresSubscription: true }} replace />;
  }

  return children;
};

/**
 * ProtectedRoute - Requires user to be logged in
 * Redirects to subscription/pricing page if not authenticated
 */
export const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const token = localStorage.getItem('token');
  
  if (!token) {
    // No free access - redirect to pricing page
    return <Navigate to="/subscription" state={{ from: location.pathname, requiresSubscription: true }} replace />;
  }
  
  return children;
};

/**
 * SubscriptionRoute - Requires active subscription
 * Redirects to subscription page if no active subscription
 */
export const SubscriptionRoute = ({ children }) => {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const { isPremium, loading: isLoading, tier } = useSubscription();
  
  // Not logged in -> redirect to subscription/pricing
  if (!token) {
    return <Navigate to="/subscription" state={{ from: location.pathname, requiresSubscription: true }} replace />;
  }
  
  // Still loading subscription status - show loading spinner
  // Wait for loading to complete before making redirect decision
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  
  // Premium users or users with premium tier can access
  if (isPremium || tier === 'premium' || tier === 'lifetime') {
    return children;
  }
  
  // Double check - maybe context hasn't updated yet, give it one more render
  // This helps with race conditions
  if (tier === undefined || tier === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  
  // No subscription -> redirect to subscription page
  return <Navigate to="/subscription" state={{ from: location.pathname, requiresSubscription: true }} replace />;
};

/**
 * FreeTrialRoute - Allows limited free access, then requires subscription
 * Used for features like Focus Timer (2 free uses)
 */
export const FreeTrialRoute = ({ children, maxFreeUses = 2, featureKey }) => {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const { isPremium, loading: isLoading } = useSubscription();
  
  // Not logged in -> redirect to subscription/pricing
  if (!token) {
    return <Navigate to="/subscription" state={{ from: location.pathname, requiresSubscription: true }} replace />;
  }
  
  // Still loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  
  // Has subscription -> allow access
  if (isPremium) {
    return children;
  }
  
  // Check free usage counter
  const usageKey = `free_usage_${featureKey}`;
  const currentUsage = parseInt(localStorage.getItem(usageKey) || '0', 10);
  
  if (currentUsage >= maxFreeUses) {
    // Free uses exhausted -> redirect to subscription
    return <Navigate to="/subscription" state={{ 
      from: location.pathname, 
      requiresSubscription: true,
      message: `Du hast deine ${maxFreeUses} kostenlosen Nutzungen aufgebraucht. Upgrade für unbegrenzten Zugang!`
    }} replace />;
  }
  
  return children;
};

export default ProtectedRoute;
